import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Inject, inject, OnDestroy, OnInit } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatSelectModule } from '@angular/material/select'
import {
  ChargeFormService,
  ChargeRowFormGroup,
  CrudService,
  EstimateChargeDiscountType,
  EstimateTemplateCatalogService,
  EstimateTemplateData,
  EstimateTemplateModel,
  Facility,
  InsuranceProviderLookup,
} from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { isEqual } from 'lodash'
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search'
import { combineLatest, filter, pairwise, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

import { ChargesTableFormModule } from '../charges-table-selector/charges-table-form.module'
import { FacilitySelectorModule } from '../facility-selector/facility-selector.module'

export interface EstimateTemplatesDialogData {
  estimateTemplate?: EstimateTemplateData
  facility?: Facility
  facilities?: Facility[]
  categoryId?: string | null
}

export interface EstimateTemplateDialogResult {
  name: string
  facilityIds: string[]
  categoryId: string | null
  charges: EstimateTemplateData['charges']
}

@Component({
  selector: 'mmx-estimate-templates-dialog',
  templateUrl: './estimate-templates-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    TranslateModule,
    ChargesTableFormModule,
    FacilitySelectorModule,
  ],
})
export class EstimateTemplatesDialogComponent implements OnInit, OnDestroy {
  private readonly chargeFormService = inject(ChargeFormService)

  public templateForm = this.fb.group({
    name: this.fb.control<InsuranceProviderLookup | string | null>(this.data.estimateTemplate?.name, Validators.required),
    facilityIds: this.fb.control<string[]>(this.data.estimateTemplate?.facilityIds || this.defaultFacilityIds),
    templateId: this.fb.control<string>(null),
    charges: this.fb.array<FormGroup<ChargeRowFormGroup>>([], Validators.required),
  })

  get chargesFormArray(): FormArray<FormGroup<ChargeRowFormGroup>> {
    return this.templateForm.controls.charges
  }

  public filterTemplateCtrl = new FormControl('')

  private onDestroy$ = new Subject<void>()

  private get defaultFacilityIds(): string[] {
    if (this.data.facilities?.length) {
      return this.data.facilities.map((facility) => facility.id)
    }

    if (this.data.facility?.id) {
      return [this.data.facility.id]
    }

    return []
  }

  private get resolvedCategoryId(): string | null {
    return this.data.estimateTemplate?.categoryId ?? this.data.categoryId ?? null
  }

  templates$ = combineLatest([
    this.filterTemplateCtrl.valueChanges.pipe(startWith(''), distinctUntilChanged()),
  ]).pipe(
    takeUntil(this.onDestroy$),
    debounceTime(300),
    switchMap(([name]) => {
      return this.estimateTemplateCatalogService.getTemplates({
        pageIndex: 0,
        pageSize: 100,
        ...(name ? { name } : {}),
      })
    }),
  )

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EstimateTemplatesDialogData,
    private dialogRef: MatDialogRef<EstimateTemplatesDialogComponent>,
    private fb: FormBuilder,
    private estimateTemplateCatalogService: EstimateTemplateCatalogService,
    private crudService: CrudService,
  ) { }

  public ngOnInit() {
    if (this.data.estimateTemplate?.charges?.length) {
      this.chargeFormService.setChargesOnFormArray(
        this.chargesFormArray,
        this.data.estimateTemplate.charges,
      )
    }

    this.templateForm.controls.facilityIds.valueChanges
      .pipe(
        filter(Boolean),
        startWith(this.templateForm.controls.facilityIds.value ?? []),
        pairwise(),
        filter(([previous, current]) => !isEqual(previous, current)),
        takeUntil(this.onDestroy$),
        tap(() => {
          this.templateForm.controls.templateId.setValue(null)
        }),
      )
      .subscribe()

    this.templateForm.controls.templateId.valueChanges
      .pipe(
        filter(Boolean),
        distinctUntilChanged(),
        takeUntil(this.onDestroy$),
        switchMap((id) => this.crudService.read(EstimateTemplateModel, id)),
      )
      .subscribe((template) => {
        if (template) {
          this.chargeFormService.setChargesOnFormArray(this.chargesFormArray, template.charges)
        }
      })

  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onAddChargeDiscountControl(event: { index: number; type: EstimateChargeDiscountType }): void {
    this.chargeFormService.addDiscountControls(this.chargesFormArray, event.index, event.type)
  }

  onRemoveChargeDiscountControl(event: { index: number }): void {
    this.chargeFormService.removeDiscountControls(this.chargesFormArray, event.index)
  }

  public onSubmit() {
    if (this.templateForm.invalid) {
      return
    }

    const { name, facilityIds } = this.templateForm.value
    const charges = this.chargeFormService
      .getChargesFromFormArray(this.chargesFormArray)
      .map(({ visitNumbers, ...charge }) => charge)

    this.dialogRef.close({
      name: String(name || '').trim(),
      facilityIds: facilityIds || this.defaultFacilityIds,
      categoryId: this.resolvedCategoryId,
      charges,
    })
  }
}
